import {
  ThresholdSchema,
  VERIFICATION_MINIMUM_SAMPLE,
  VERIFICATION_SETTING_BOUNDS,
  thresholdMilli,
  type VerificationBasis,
  type VerificationGrain,
  type VerificationPolicyField,
  type VerificationPolicyMode,
  type VerificationPolicyState,
  type VerificationSettingChange,
} from '@clm/shared-types';

import { ApiError } from '../../api/errors';
import { classifyApiError, type ApiProblemKind } from '../../api/problem';
import { refusalCodeOf } from '../base-msa-shared';

/**
 * The pure half of the Admin **Auto-approval** surface — CLM-108 (FR-31, FR-32,
 * NFR-16, CR-06; D-2.1-1 … D-2.1-7; R-108-3, R-108-10 … R-108-12, R-108-17,
 * R-108-20, R-108-24). The `pipeline-runs/shared.ts` split: the page's
 * components export only components (fast refresh), and the copy, the role
 * hints and the refusal words have tests of their own.
 *
 * Role lists are *display hints* (`mayAttempt`, `permissions.ts`): every
 * endpoint enforces its own action and the Scope-All check (NFR-06). An
 * identity that names no role sees every control and the API's 403 is the
 * answer (D-82-1).
 */

/* -------------------------------------------------------------------------- */
/* Who may do what — display hints                                            */
/* -------------------------------------------------------------------------- */

/** `admin:configure` — the page's own read, the per-field change, propose and withdraw. */
export const POLICY_ADMIN_ROLES: readonly string[] = ['admin'];
/** `compliance:cosign_verification_settings` — Legal's read and both co-signs (R-108-11). */
export const POLICY_LEGAL_ROLES: readonly string[] = ['legal_reviewer'];

/* -------------------------------------------------------------------------- */
/* Copy                                                                        */
/* -------------------------------------------------------------------------- */

/** Neither read answered: the caller holds neither action. */
export const VERIFICATION_POLICY_FORBIDDEN_COPY =
  'Auto-approval is configured by administrators (admin:configure) and made effective by CEQ-Legal ' +
  '(compliance:cosign_verification_settings). Your role holds neither; the verification queue is unchanged for you.';

/** No effective settings revision: no field can auto-approve (D-2.1-7, R-108-10 — there is no seed row). */
export const AUTO_APPROVAL_OFF_COPY =
  'Auto-approval is off — no threshold has been co-signed (D-2.1-7). Requirement default: 0.900.';

/** The practice-scoped Admin's notice, in place of the per-field table (R-108-12). */
export const SCOPE_ALL_REQUIRED_COPY =
  'This page uses the organisation-wide figure; it needs Scope-All. Your scoped figures are on Extraction accuracy.';

/** The practice-scoped Legal holder's notice, in place of the opt-ins awaiting co-sign (R-108-12). */
export const OPT_INS_SCOPE_ALL_COPY =
  'Opt-ins rest on the organisation-wide figure; co-signing one needs Scope-All.';

/** The proposer of a settings change cannot co-sign it — a dual-role holder included (D-2.1-7). */
export const PROPOSER_CANNOT_COSIGN_COPY =
  'You proposed this change; a different Legal holder must co-sign it.';

/** The Admin who set an opt-in cannot co-sign it (R-108-24). */
export const SETTER_CANNOT_COSIGN_COPY = 'You set this opt-in; a different Legal holder must co-sign it.';

/**
 * CLM-127, D-127-1 — never confusable with a genuine Legal co-sign, wherever a decided row is
 * shown: the settings card's "In force" line and its History column, and the field table's opt-in
 * display (R-127-16). `decidedByCopy` composes it with the decider's UPN; both are exported because
 * the bare words are also asserted on their own, by a test that must not depend on a fixture UPN.
 */
export const SELF_CERTIFIED_BADGE_COPY = 'not independently reviewed';

/**
 * "self-certified by {upn} — not independently reviewed" or "co-signed by {upn}" (R-127-16),
 * lower case so it reads in place of the existing "co-signed by" phrase, never capitalised
 * differently depending on which happened.
 */
export function decidedByCopy(upn: string, isSelfCertified: boolean): string {
  return isSelfCertified ? `self-certified by ${upn} — ${SELF_CERTIFIED_BADGE_COPY}` : `co-signed by ${upn}`;
}

/** Said when `auto_approve` is chosen in the change dialog (R-108-24). */
export const OPT_IN_TAKES_EFFECT_COPY =
  'Takes effect when a different Legal holder co-signs it (D-2.1-7). Until then the field follows the threshold.';

/** Why the opt-in needs a reason: it lets a field skip review below the co-signed threshold. */
export const OPT_IN_NOTE_COPY =
  'An opt-in needs a reason: it lets this field skip review below the co-signed threshold, and the reason is recorded ' +
  'with the revision Legal is asked to co-sign.';

/** A high-stakes field accepts no policy row at all (D-2.1-5). */
export const HIGH_STAKES_COPY = 'FR-07: always reviewed by a person';

/** A rate below the minimum sample — never a number (CLM-107's words, R-107-7). */
export const TOO_FEW_DECISIONS_COPY = 'too few decisions';

/** The values the first proposal is pre-filled with (R-108-9, R-108-10). Only 0.900 is decided; the others are R-108-9's defaults. */
export const SETTINGS_PREFILL = { threshold: '0.900', sample_rate: '0.200', stale_after_days: 90 } as const;

/** The mode's words. `threshold` at revision 0 is the default every field starts with (R-108-3). */
export const MODE_LABELS: Readonly<Record<VerificationPolicyMode, string>> = {
  threshold: 'Threshold',
  always_verify: 'Always a person',
  auto_approve: 'Opt-in below the threshold',
};

/** What each mode does, in the change dialog. */
export const MODE_HELP: Readonly<Record<VerificationPolicyMode, string>> = {
  threshold:
    'Auto-approves when the field has at least 30 human decisions at its current grain and its rate meets the ' +
    'co-signed threshold; a sampled share still goes to a person (NFR-16). Takes effect at once.',
  always_verify: 'Every value goes to a person, at every grain, whatever the rate. Takes effect at once.',
  auto_approve:
    'Auto-approves below the threshold, at this grain only, still with at least 30 decisions and the sample. ' +
    'A new schema version, model or definition lapses it.',
};

/**
 * The effective state: a neutral tag and the reason in words (§4.7). The page's
 * state is the pipeline's decision with the sample step dropped, so `auto`
 * means "auto-approves, except the share the sample sends to a person".
 */
export const STATE_COPY: Readonly<Record<VerificationPolicyState, { readonly tag: string; readonly reason: string }>> = {
  auto: {
    tag: 'Auto-approves',
    reason: 'Meets the policy over the minimum sample. The sample still sends a share of its values to a person.',
  },
  high_stakes_exempt: { tag: 'Always reviewed', reason: 'High stakes (FR-07): reviewed by a person whatever its rate.' },
  admin_pinned: { tag: 'Always reviewed', reason: 'Pinned by an administrator: every value goes to a person.' },
  not_configured: { tag: 'Off', reason: 'No threshold has been co-signed, so every value goes to a person.' },
  too_few_decisions: {
    tag: 'Queues',
    reason: `Fewer than ${String(VERIFICATION_MINIMUM_SAMPLE)} human decisions at its current grain (D-2.1-2).`,
  },
  stale: {
    tag: 'Queues — stale',
    reason: 'No human decision within the window, so every value goes to a person until one lands (NFR-16).',
  },
  below_threshold: { tag: 'Queues', reason: 'Below the threshold at its current grain.' },
  not_extracted: { tag: 'Not extracted', reason: 'No pipeline extraction at any grain yet, or the field is retired.' },
};

/* -------------------------------------------------------------------------- */
/* Formatting — the API's figures, never recomputed                           */
/* -------------------------------------------------------------------------- */

/** `1.5.0 · claude-… · definition r3` — the grain names the definition for an admin field (R-108-6). */
export function grainCopy(grain: VerificationGrain | null): string {
  if (grain === null) return 'never extracted';
  const parts = [grain.extraction_schema_version, grain.extraction_model];
  if (grain.definition_revision !== null) parts.push(`definition r${String(grain.definition_revision)}`);
  return parts.join(' · ');
}

/** The rate as the API rendered it (three places), or "too few decisions" below the minimum — never a number. */
export function rateCopy(basis: VerificationBasis, minimum: number): string {
  return basis.sample_size < minimum || basis.accuracy_rate === null ? TOO_FEW_DECISIONS_COPY : basis.accuracy_rate;
}

/** `29 / 2 / 1` with its spoken form. */
export function decisionsSpoken(basis: VerificationBasis): string {
  return `${String(basis.verified_count)} verified, ${String(basis.corrected_count)} corrected, ${String(basis.rejected_count)} rejected`;
}

/**
 * The opt-in's standing, in the policy cell (§4.7): awaiting co-sign, declined,
 * co-signed by whom — and lapsed, which outranks the rest because a lapsed
 * opt-in behaves as `threshold` whatever Legal decided. `null` for any other mode.
 */
export function optInCopy(policy: VerificationPolicyField['policy']): string | null {
  if (policy.mode !== 'auto_approve') return null;
  if (policy.opt_in_lapsed) return 'opt-in lapsed — taken at another grain, so the field follows the threshold';
  if (policy.cosign === null) return 'opt-in awaiting co-sign';
  if (policy.cosign.decision === 'decline') return `opt-in declined by ${policy.cosign.decided_by_upn}`;
  /* CLM-127, D-127-1 — self-certified is never confusable with a genuine co-sign (R-127-16);
   * `decidedByCopy` is already lower case, so it reads in place of the "co-signed by" phrase. */
  return `opt-in ${decidedByCopy(policy.cosign.decided_by_upn, policy.cosign.is_self_certified)}`;
}

/** Whole days between two instants the API rendered — the pending change's age, against the response's own clock. */
export function ageInDays(fromUtc: string, nowUtc: string): number {
  return Math.max(0, Math.floor((Date.parse(nowUtc) - Date.parse(fromUtc)) / 86_400_000));
}

export function ageCopy(fromUtc: string, nowUtc: string): string {
  const days = ageInDays(fromUtc, nowUtc);
  if (days === 0) return 'today';
  return days === 1 ? '1 day ago' : `${String(days)} days ago`;
}

/** The three settings values, as one line. */
export function settingsLine(change: Pick<VerificationSettingChange, 'threshold' | 'sample_rate' | 'stale_after_days'>): string {
  return `threshold ${change.threshold} · sample ${change.sample_rate} · stale after ${String(change.stale_after_days)} days`;
}

/* -------------------------------------------------------------------------- */
/* The propose form's own checks — the API's bounds, said before it is asked   */
/* -------------------------------------------------------------------------- */

export interface SettingsDraft {
  readonly threshold: string;
  readonly sample_rate: string;
  readonly stale_after_days: string;
  readonly proposal_note: string;
}

/**
 * The first thing wrong with a draft, in words, or `null`. The bounds are the
 * database's sanity bounds (R-108-9, `VERIFICATION_SETTING_BOUNDS`), compared
 * as integer thousandths — never as floats (R-108-5). The API checks the same
 * and answers with a coded refusal; this only saves a round trip.
 */
export function settingsDraftProblem(draft: SettingsDraft): string | null {
  if (!ThresholdSchema.safeParse(draft.threshold).success) return 'The threshold is a three-decimal rate, such as 0.900.';
  if (!ThresholdSchema.safeParse(draft.sample_rate).success) return 'The sample rate is a three-decimal rate, such as 0.200.';
  if (thresholdMilli(draft.threshold) <= thresholdMilli(VERIFICATION_SETTING_BOUNDS.threshold_above)) {
    return `The threshold must be above ${VERIFICATION_SETTING_BOUNDS.threshold_above}.`;
  }
  if (thresholdMilli(draft.sample_rate) < thresholdMilli(VERIFICATION_SETTING_BOUNDS.sample_rate_min)) {
    return `The sample rate must be at least ${VERIFICATION_SETTING_BOUNDS.sample_rate_min} — NFR-16 keeps a share of every auto field going to a person.`;
  }
  const days = Number(draft.stale_after_days);
  if (
    !/^\d+$/.test(draft.stale_after_days) ||
    days < VERIFICATION_SETTING_BOUNDS.stale_after_days_min ||
    days > VERIFICATION_SETTING_BOUNDS.stale_after_days_max
  ) {
    return `The stale window is a whole number of days from ${String(VERIFICATION_SETTING_BOUNDS.stale_after_days_min)} to ${String(VERIFICATION_SETTING_BOUNDS.stale_after_days_max)}.`;
  }
  if (draft.proposal_note.trim() === '') return 'A proposal needs a note — Legal reads it before co-signing.';
  return null;
}

/* -------------------------------------------------------------------------- */
/* Refusals — the page's own words for the API's codes                         */
/* -------------------------------------------------------------------------- */

/** A refusal the page explains in its own words. `up` for a 409 (nothing failed, the state moved), `bad` for a 400 or 404. */
export interface PolicyRefusalCopy {
  readonly code: string;
  readonly tone: 'up' | 'bad';
  readonly head: string;
  readonly body: string;
  /** The state moved under the page: re-read both views after showing this. */
  readonly reload: boolean;
}

/** A failed write as the page holds it. `refusal: null` is everything else, which `ErrorNotice` renders by kind. */
export interface PolicyProblem {
  readonly kind: ApiProblemKind;
  readonly refusal: PolicyRefusalCopy | null;
  readonly correlation_id: string | null;
}

type Copy = { readonly head: string; readonly body: string; readonly reload?: boolean };

/** 400 — the change cannot be made as asked. */
const VALIDATION_COPY: Readonly<Record<string, Copy>> = Object.freeze({
  high_stakes_exempt: {
    head: 'A high-stakes field takes no policy',
    body: 'High-stakes fields are always reviewed by a person (D-2.1-5, FR-07), so no policy can be recorded for one.',
  },
  no_change: { head: 'Nothing to save', body: 'That is already the policy in force. Nothing was recorded.' },
  note_required: {
    head: 'A note is required',
    body: 'An opt-in and a decline each record why. Add a note, then try again.',
  },
  threshold_out_of_bounds: {
    head: 'The threshold is out of bounds',
    body: `A threshold is above ${VERIFICATION_SETTING_BOUNDS.threshold_above} and at most ${VERIFICATION_SETTING_BOUNDS.threshold_max}.`,
  },
  sample_rate_out_of_bounds: {
    head: 'The sample rate is out of bounds',
    body: `A sample rate is from ${VERIFICATION_SETTING_BOUNDS.sample_rate_min} to ${VERIFICATION_SETTING_BOUNDS.sample_rate_max}: NFR-16 forbids 0.`,
  },
  stale_window_out_of_bounds: {
    head: 'The stale window is out of bounds',
    body: `A stale window is from ${String(VERIFICATION_SETTING_BOUNDS.stale_after_days_min)} to ${String(VERIFICATION_SETTING_BOUNDS.stale_after_days_max)} days.`,
  },
});

/** 409 — the state does not admit the change, or it moved. */
const CONFLICT_COPY: Readonly<Record<string, Copy>> = Object.freeze({
  field_retired: {
    head: 'That field is retired',
    body: 'A retired admin field is no longer extracted, so its policy cannot change. Reactivate it on Extracted fields first.',
  },
  too_few_decisions: {
    head: 'Too few decisions for an opt-in',
    body: `An opt-in still needs at least ${String(VERIFICATION_MINIMUM_SAMPLE)} human decisions at the field's current grain (D-2.1-2).`,
  },
  policy_changed: {
    head: 'The field’s policy changed since this page loaded',
    body: 'Another change was saved first, so nothing was recorded. The page has been reloaded — check it, then decide again.',
    reload: true,
  },
  not_opt_in: {
    head: 'That revision is not an opt-in',
    body: 'Only an opt-in below the threshold is co-signed. The page has been reloaded.',
    reload: true,
  },
  opt_in_lapsed: {
    head: 'That opt-in has lapsed',
    body: 'It was taken at another schema version, model or definition, so a co-sign could never apply. Nothing was recorded.',
    reload: true,
  },
  cosigner_is_proposer: {
    head: 'A different person must co-sign',
    body: 'You made this change, so you cannot also make it effective — whichever role you hold (D-2.1-7). Nothing was recorded.',
  },
  already_decided: {
    head: 'Already decided',
    body: 'Another Legal holder decided this first. The page has been reloaded.',
    reload: true,
  },
  change_pending: {
    head: 'A change is already pending',
    body: 'Only one settings change can await a co-sign at a time. Withdraw it, or wait for Legal to decide it.',
    reload: true,
  },
  settings_changed: {
    head: 'The settings changed since this page loaded',
    body: 'A different revision took effect first, so nothing was proposed. The page has been reloaded — check it, then propose again.',
    reload: true,
  },
  not_pending: {
    head: 'That change is no longer pending',
    body: 'It was co-signed, declined or withdrawn first. The page has been reloaded.',
    reload: true,
  },
  stale_view: {
    head: 'The values changed under you',
    body: 'The pending change no longer holds the values you were shown, so nothing was co-signed. The page has been reloaded — read it again.',
    reload: true,
  },
});

const NOT_FOUND_COPY: Copy = {
  head: 'That field does not exist',
  body: 'It is not a governed field and was never added as an admin field.',
};

/**
 * A failed write as the page shows it. A code is trusted only with the status
 * the API sends it under, so a stray field error cannot borrow its words — the
 * CLM-109 `fieldSetProblemOf` rule.
 */
export function policyProblemOf(error: unknown): PolicyProblem {
  const kind = classifyApiError(error);
  const code = refusalCodeOf(error);
  const correlation_id = error instanceof ApiError ? error.correlation_id : null;
  const of = (found: Copy | undefined, refusalCode: string, tone: 'up' | 'bad'): PolicyProblem | null =>
    found === undefined
      ? null
      : { kind, correlation_id, refusal: { code: refusalCode, tone, head: found.head, body: found.body, reload: found.reload === true } };

  if (kind === 'validation' && code !== null) {
    const problem = of(VALIDATION_COPY[code], code, 'bad');
    if (problem !== null) return problem;
  }
  if (kind === 'conflict' && code !== null) {
    const problem = of(CONFLICT_COPY[code], code, 'up');
    if (problem !== null) return problem;
  }
  if (kind === 'not_found') {
    const problem = of(NOT_FOUND_COPY, 'field_not_found', 'bad');
    if (problem !== null) return problem;
  }
  return { kind, correlation_id, refusal: null };
}
